import { useQuery } from '@tanstack/react-query';
import { Badge } from '../../../components/ui';
import { formatDate, formatQuantity, humanise } from '../../../lib/format';
import { expiringLotsQuery } from '../queries';
import { WidgetCard, WidgetEmpty, WidgetError, WidgetList, WidgetRow, WidgetSkeleton, RowTitle } from '../primitives';

/**
 * Lots whose expiry is near. The API filters by date but orders by item and lot
 * code, so the soonest expiry is picked out here from a bounded page rather than
 * trusting the first few rows the server happens to return.
 */
export function ExpiringWidget({
  days = 30,
  limit = 5,
  to,
}: {
  days?: number;
  limit?: number;
  to?: string;
}) {
  const query = useQuery(expiringLotsQuery(days));

  const rows = (query.data?.items ?? [])
    .filter((lot) => lot.expiryDate !== null)
    .sort((a, b) => (a.expiryDate as string).localeCompare(b.expiryDate as string))
    .slice(0, limit);

  const total = (query.data?.items ?? []).filter((lot) => lot.expiryDate !== null).length;

  return (
    <WidgetCard
      title="Expiring soon"
      description={`Lots that expire within ${days} days.`}
      to={to}
      toLabel="Open inventory"
      badge={total > 0 ? <Badge tone="hazard">{total}</Badge> : null}
    >
      {query.isPending ? <WidgetSkeleton rows={2} /> : null}
      {query.isError ? (
        <WidgetError label="Could not load expiry." onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && rows.length === 0 ? (
        <WidgetEmpty>Nothing expires in the next {days} days.</WidgetEmpty>
      ) : null}
      {rows.length > 0 ? (
        <WidgetList>
          {rows.map((lot) => (
            <WidgetRow key={lot.id}>
              <RowTitle
                title={lot.code}
                subtitle={
                  <>
                    <span className="font-mono">{lot.itemCode}</span> · {humanise(lot.status)}
                  </>
                }
              />
              <div className="shrink-0 text-right">
                <p className="whitespace-nowrap text-sm font-semibold text-ink">
                  {formatQuantity(lot.onHand, lot.baseUom)}
                </p>
                <p className="whitespace-nowrap text-xs text-ink-muted">
                  expires {formatDate(lot.expiryDate)}
                </p>
              </div>
            </WidgetRow>
          ))}
        </WidgetList>
      ) : null}
    </WidgetCard>
  );
}
